import QRCode from "qrcode";
import { AdJourneyGuide } from "./ad-journey-guide";
import {
  applySlotIframeStyles,
  classifyBannerDimensions,
  clearSlotIframeStyles,
  defaultSlotDimensions,
  parsePositiveInt,
  shouldDefaultTabletDevice,
} from "./banner-preview-layout";
import {
  dismissClickthroughOverlays,
  handleRawAssetCtaMessage,
} from "../clickthrough-overlay";
import {
  getPreviewersBaseUrl,
  getPreviewersOrigin,
} from "../previewer-static-base";
import "./styles.css";

/** Escape a string for use as HTML text content. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Escape a string for use inside a double-quoted HTML attribute value. */
function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

const viewMasterEndpoint =
  "https://cdn-lb.vungle.com/vm-embed/latest/index.html";

interface ScreenSize {
  width: number;
  height: number;
}

type ProductFeed = {
  id: number;
  name: string;
  products: unknown;
};

interface TrePreview {
  setLoading: (isLoading: boolean) => void;
  adJourneyGuide: AdJourneyGuide | null;
  bidResponse: string | null;
  creativeName: string;
  isRawAsset: boolean;
  isMadlib: boolean;
  isViewMaster: boolean;
  isNative: boolean;
  forceLandscape: boolean;
  screenSizes: {
    mobile: ScreenSize;
    mobileX: ScreenSize;
    tablet: ScreenSize;
  };
  creativeId: string;
  previewPortrait: boolean;
  previewDevice: number;
  reloadEnabled: boolean;
  creativeInfoInitialized: boolean;
  useFullscreenPreview: boolean;
  headerLeftWidth: number;
  products: ProductFeed[];
  contentType: "fullscreen" | "mrec" | "banner";
  onRotate: () => void;
  onChangeDevice: () => void;
  onRefresh: () => void;
  populateCreativeInfo: () => void;
  languageChanged: () => void;
  productFeedChanged: () => void;
  enableReload: (enable: boolean) => void;
  toggleOrientation: () => void;
  toggleDevice: () => void;
  applyDeviceLayout: () => void;
  applyBannerSlotChrome: (
    width: number,
    height: number,
    contentType: "banner" | "mrec",
    searchParams: URLSearchParams
  ) => void;
  applyNativeFooter: (searchParams: URLSearchParams) => void;
  buildNativeFooterHtml: (searchParams: URLSearchParams) => string;
  clearNativeFooter: () => void;
  showStateMachinePanel: () => void;
  langSelector: () => HTMLSelectElement;
  productFeedSelector: () => HTMLSelectElement;
  reloadCreative: (refreshClicked: boolean) => void;
  generateQrCode: (lang: string | null) => void;
  onResize: () => void;
  determineHeaderVisibility: () => void;
  getSearchParams: () => URLSearchParams;
  setIframeSource: (iframe: HTMLElement, src: string) => void;
  sendProductsToIframe: (iframe: HTMLIFrameElement) => void;
}

declare global {
  interface Window {
    TrePreview: TrePreview;
  }
}

const synchronousRequest = (url: string): string => {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", url, false);
  xhr.send(null);
  if (xhr.status === 200) {
    return xhr.responseText;
  }
  throw new Error(`Request failed: ${xhr.statusText}`);
};

window.TrePreview = {
  setLoading(isLoading: boolean) {
    const loader = document.getElementById("loader");
    const previewContainer = document.getElementById("preview_container");

    if (!loader || !previewContainer) {
      console.error("Loader or preview container not found!");
      return;
    }

    if (isLoading) {
      loader.style.display = "block";
      previewContainer.style.visibility = "hidden";
    } else {
      loader.style.display = "none";
      previewContainer.style.visibility = "visible";
    }
  },
  adJourneyGuide: null,
  bidResponse: null,
  useFullscreenPreview: true,
  creativeName: "",
  creativeId: "",
  isRawAsset: false,
  isMadlib: false,
  isViewMaster: false,
  isNative: false,
  forceLandscape: false,
  screenSizes: {
    mobile: {
      width: 375,
      height: 667,
    },
    mobileX: {
      width: 428,
      height: 845,
    },
    tablet: {
      width: 820,
      height: 1180,
    },
  },
  previewPortrait: true,
  previewDevice: 0,
  reloadEnabled: false,
  creativeInfoInitialized: false,
  headerLeftWidth: 0,
  contentType: "fullscreen",
  products: [], // Add the missing 'products' property
  setIframeSource(iframe: HTMLElement, src: string) {
    iframe.setAttribute("src", src); // Add the missing 'setIframeSource' method
  },
  onRotate() {
    // NOTE(Luis): Prevent rotation for banner and mrec.
    if (this.contentType === "banner" || this.contentType === "mrec") {
      return;
    }
    this.toggleOrientation();
  },
  onChangeDevice() {
    this.toggleDevice();
  },
  onRefresh() {
    if (!this.reloadEnabled) {
      return;
    }

    // Disable the reload button while the creative is reloading, otherwise things can get weird.
    this.enableReload(false);

    // Kickoff the reload
    this.reloadCreative(true);
  },

  populateCreativeInfo() {
    if (this.creativeInfoInitialized) {
      return;
    }

    if (this.useFullscreenPreview) {
      const header = document.getElementById("preview_header") as HTMLElement;
      const panels = document.getElementById("panel_holder") as HTMLElement;
      header.style.display = "none";
      panels.style.display = "none";
    } else {
      const mobileContainer = document.getElementById(
        "content-container-mobile"
      ) as HTMLElement;
      mobileContainer.style.display = "none";
    }

    const searchParams = this.getSearchParams();

    if (searchParams && searchParams.has("size")) {
      const formatSize = searchParams.get("size");
      this.contentType = formatSize as "fullscreen" | "mrec" | "banner";
    }

    // Creative name
    const creativeNameEl = document.getElementById(
      "creative_name"
    ) as HTMLElement;
    creativeNameEl.innerHTML = this.creativeName;

    // Status (disabling this for now as we no longer have access to status without CreativeServer)
    const statusBar = document.getElementById("header_left") as HTMLElement;
    // statusBar.style.visibility = 'hidden';
    this.headerLeftWidth = statusBar.clientWidth;

    if (this.isRawAsset) {
      this.determineHeaderVisibility();
      this.creativeInfoInitialized = true;
      return;
    }

    // Do not display product or language select dropdowns for VM creatives since these will
    // pull product and language data from the AdMarkup bid context.
    if (!this.isViewMaster && !this.isNative) {
      // Language - only enable the widget if the creative supports >1 language
      const langWidget = document.getElementById(
        "language_widget"
      ) as HTMLElement;
      let languages: {
        code: string;
        label: string;
        defaultLanguage: boolean;
      }[] = [];
      // If this is not a native Madlib endcard then pull available languages from
      // the API Server.
      if (!this.isMadlib) {
        let requestResults;
        try {
          requestResults = synchronousRequest(
            `https://api${
              BUILD_ENV === "development" ? "-dev" : ""
            }.tresensa.com/creatives/languages/${this.creativeId}`
          );
        } catch (error) {
          console.error(error);
        }
        if (requestResults) {
          languages = JSON.parse(requestResults).languages;
        }
      }

      // If there are languages available for this HTML endcard then display
      // languages dropdown.
      const showLang = languages.length > 0;
      if (showLang) {
        langWidget.style.display = "block";

        let defaultLanguageCode: string | undefined;

        for (let i = 0; i < languages.length; i++) {
          const lang = languages[i];
          const option = document.createElement("option");
          option.value = lang.code;
          option.innerHTML = lang.label;
          this.langSelector().appendChild(option);
          if (lang.defaultLanguage) {
            defaultLanguageCode = lang.code;
          }
        }

        let langValue = "";
        // If URL has language value then set this value to the dropdown.
        if (searchParams && searchParams.has("lang")) {
          langValue = searchParams.get("lang") || "";
        } else if (typeof defaultLanguageCode !== undefined) {
          langValue = defaultLanguageCode || "";
        }
        this.langSelector().value = langValue;

        this.langSelector().addEventListener(
          "change",
          window.TrePreview.languageChanged.bind(this)
        );

        // Show it
        langWidget.style.display = "inline";
      } else {
        langWidget.style.display = "none";
      }

      let productFetchURL = `${TRE_MANAGER_URL}/getCreativeFeeds?treCreativeID=${this.creativeId}`;

      if (searchParams.has("region")) {
        productFetchURL += `&region=${searchParams.get("region")}`;
      }

      // Pull feeds
      const feedsRequest = synchronousRequest(productFetchURL);
      window.TrePreview.products = JSON.parse(feedsRequest) as ProductFeed[];

      const displayProductFeeds = window.TrePreview.products.length > 0;

      if (displayProductFeeds) {
        window.TrePreview.products.forEach((feed: ProductFeed) => {
          const option = document.createElement("option");
          option.value = String(feed.id);
          option.innerHTML = feed.name;
          this.productFeedSelector().appendChild(option);
        });
        this.productFeedSelector().style.display = "inline";
        this.productFeedSelector().addEventListener(
          "change",
          window.TrePreview.productFeedChanged.bind(this)
        );
        this.reloadCreative(false);
      }
    }

    // If the header width is too tight, hide the creative status
    this.determineHeaderVisibility();

    this.creativeInfoInitialized = true;
  },

  languageChanged() {
    // Update QR code with the lang parameter included, so if it's opened on mobile it will
    // retain this selected language.
    this.generateQrCode(this.langSelector().value);

    // Reload the creative
    this.reloadCreative(false);
  },

  productFeedChanged() {
    // Reload the creative
    this.reloadCreative(false);
  },

  enableReload(enable: boolean) {
    this.reloadEnabled = enable;

    const reloadIcon = document.getElementById("refresh_button") as HTMLElement;
    reloadIcon.style.opacity = this.reloadEnabled ? "1.0" : "0.4";
  },

  toggleOrientation() {
    this.previewPortrait = !this.previewPortrait;
    const previewDeviceContainer = document.getElementById(
      "preview_device_container"
    ) as HTMLElement;
    if (this.previewPortrait) {
      previewDeviceContainer.classList.replace("landscape", "portrait");
    } else {
      previewDeviceContainer.classList.replace("portrait", "landscape");
    }

    this.applyDeviceLayout();
  },

  toggleDevice() {
    this.previewDevice = (this.previewDevice + 1) % 3;
    const previewDeviceContainer = document.getElementById(
      "preview_device_container"
    );

    switch (this.previewDevice) {
      case 0:
        previewDeviceContainer?.classList.remove("small_phone", "tablet");
        previewDeviceContainer?.classList.add("large_phone");
        break;
      case 1:
        previewDeviceContainer?.classList.remove("large_phone", "small_phone");
        previewDeviceContainer?.classList.add("tablet");
        break;
      case 2:
        previewDeviceContainer?.classList.remove("large_phone", "tablet");
        previewDeviceContainer?.classList.add("small_phone");
        break;
      default:
        break;
    }

    this.applyDeviceLayout();
  },

  applyBannerSlotChrome(
    width: number,
    height: number,
    contentType: "banner" | "mrec",
    searchParams: URLSearchParams
  ) {
    this.contentType = contentType;

    const previewDeviceContainer = document.getElementById(
      "preview_device_container"
    );
    previewDeviceContainer?.classList.remove("fullscreen", "mrec", "banner");
    previewDeviceContainer?.classList.add(this.contentType);

    applySlotIframeStyles(contentType, width, height);

    if (shouldDefaultTabletDevice(width, height, searchParams.get("device"))) {
      this.previewDevice = 1;
      previewDeviceContainer?.classList.remove("small_phone", "large_phone");
      previewDeviceContainer?.classList.add("tablet");
    }

    if (!this.previewPortrait) {
      this.previewPortrait = true;
      previewDeviceContainer?.classList.replace("landscape", "portrait");
    }

    const rotateButton = document.getElementById("rotate_button");
    if (rotateButton) {
      rotateButton.style.display = "none";
    }

    this.applyDeviceLayout();
  },

  applyDeviceLayout() {
    if (this.useFullscreenPreview) {
      const mobileContainer = document.getElementById(
        "content-container-mobile"
      ) as HTMLElement;
      mobileContainer.classList.remove("fullscreen", "mrec", "banner");
      mobileContainer.classList.add(this.contentType);

      if (document.documentElement.clientWidth > 480) {
        mobileContainer.classList.add("tablet");
      }
    }

    const previewDeviceContainer = document.getElementById(
      "preview_device_container"
    ) as HTMLElement;
    if (this.isMadlib || this.contentType !== "fullscreen" || this.isNative) {
      const rotateButton = document.getElementById(
        "rotate_button"
      ) as HTMLElement;
      rotateButton.style.display = "none";
      if (this.forceLandscape) {
        previewDeviceContainer?.classList.replace("portrait", "landscape");
        this.previewPortrait = false;
      }
    }

    if (this.isNative) {
      const deviceButton = document.getElementById(
        "device_button"
      ) as HTMLElement;
      deviceButton.style.display = "none";
    }

    previewDeviceContainer.classList.remove("fullscreen", "mrec", "banner");
    previewDeviceContainer.classList.add(this.contentType);

    const deviceStr =
      /* eslint-disable no-nested-ternary */
      this.previewDevice === 0
        ? "mobileX"
        : this.previewDevice === 1
          ? "tablet"
          : "mobile";

    // Screen size
    const size = this.screenSizes[deviceStr];
    const width = this.previewPortrait ? size.width : size.height;
    const height = this.previewPortrait ? size.height : size.width;

    const screenContainer = document.getElementById(
      "screen_container"
    ) as HTMLElement;
    const frameScale = Math.min(
      screenContainer.offsetWidth / width,
      screenContainer.offsetHeight / height
    );

    const container = document.getElementById(
      "content-container-desktop"
    ) as HTMLElement;
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.transform = `
            scale(${frameScale},${frameScale})
            translate(${-50 / frameScale}%, ${-50 / frameScale}%)`;
  },

  /**
   * Render the Labrat-style Native ad footer (app icon + title + description +
   * CTA) inside the device frame, below the hero, for an *unpublished* native
   * asset. Published natives get the footer from Labrat (the `creative=`
   * path); unpublished natives use a raw CDN `asset=` URL with no Pinpoint CID,
   * so the approvals-dash `/p/{uuid}` resolver passes `native=1&icon=…&title=…&desc=…&cta=…`
   * and TrePreview renders the footer itself. Markup/colors mirror CE's
   * `NativeAdFooter` (detail variant) so the visual matches the dash.
   */
  applyNativeFooter(searchParams: URLSearchParams) {
    this.isNative = true;

    // Native is a fixed landscape unit — hide the device toggle (matches the
    // published-native / Labrat path at `if (this.isNative)` in applyDeviceLayout).
    const deviceButton = document.getElementById(
      "device_button"
    ) as HTMLElement | null;
    if (deviceButton) deviceButton.style.display = "none";

    const footerHtml = this.buildNativeFooterHtml(searchParams);

    const containers = [
      document.getElementById("content-container-desktop"),
      document.getElementById("content-container-mobile"),
    ];
    containers.forEach((el) => {
      const container = el as HTMLElement | null;
      if (!container) return;
      container.classList.add("native");
      // Remove any footer from a previous render.
      container.querySelectorAll(".native-footer").forEach((f) => f.remove());
      const wrapper = container.querySelector(".iframe-wrapper");
      if (wrapper) {
        wrapper.insertAdjacentHTML("afterend", footerHtml);
      } else {
        container.insertAdjacentHTML("beforeend", footerHtml);
      }
    });
  },

  buildNativeFooterHtml(searchParams: URLSearchParams): string {
    const icon = (searchParams.get("icon") || "").trim();
    const title = (searchParams.get("title") || "").trim();
    const desc = (searchParams.get("desc") || "").trim();
    const cta = (searchParams.get("cta") || "INSTALL").trim().toUpperCase();
    // Truncate the title to 25 unicode code points (matches NativeAdFooter /
    // PublishModal nativeTitle).
    const titleTrunc = Array.from(title).slice(0, 25).join("");

    const iconHtml = icon
      ? `<img class="native-footer__icon" src="${escapeAttr(icon)}" alt="" />`
      : `<div class="native-footer__icon-placeholder" aria-hidden="true"></div>`;
    const titleHtml = titleTrunc
      ? `<p class="native-footer__title">${escapeHtml(titleTrunc)}</p>`
      : `<p class="native-footer__title native-footer__title--placeholder">No app title</p>`;
    const descHtml = desc
      ? `<p class="native-footer__desc" title="${escapeAttr(desc)}">${escapeHtml(desc)}</p>`
      : `<p class="native-footer__desc native-footer__desc--placeholder">No description</p>`;

    return `<footer class="native-footer">${iconHtml}<div class="native-footer__text">${titleHtml}${descHtml}</div><span class="native-footer__cta">${escapeHtml(cta)}</span></footer>`;
  },

  /**
   * Remove any native footer rendered by a previous `applyNativeFooter` call and
   * reset the `native` layout class. Called at the start of `reloadCreative` so
   * a switch from a native asset to a non-native asset (or a non-native footer
   * render) doesn't leave stale footer chrome behind.
   */
  clearNativeFooter() {
    this.isNative = false;
    document.querySelectorAll(".native-footer").forEach((f) => f.remove());
    document
      .querySelectorAll("#content-container-desktop, #content-container-mobile")
      .forEach((c) => c.classList.remove("native"));
    // Restore the device toggle visibility (applyNativeFooter hides it).
    const deviceButton = document.getElementById(
      "device_button"
    ) as HTMLElement | null;
    if (deviceButton) deviceButton.style.display = "";
  },

  showStateMachinePanel() {
    const panelHolder = document.getElementById("panel_holder");
    panelHolder?.classList.remove("middle-hidden");

    // Adjust the device layout
    this.applyDeviceLayout();
  },

  langSelector() {
    return document.getElementById("language_selector") as HTMLSelectElement;
  },

  productFeedSelector() {
    return document.getElementById(
      "product_feed_selector"
    ) as HTMLSelectElement;
  },

  getSearchParams() {
    return new URLSearchParams(window.location.search);
  },

  sendProductsToIframe(iframe: HTMLIFrameElement) {
    if (this.productFeedSelector() && this.productFeedSelector().value) {
      const selectedProducts = window.TrePreview.products
        .filter((v) => v.id === Number(this.productFeedSelector().value))
        .map((v) => v.products);

      const productsData = selectedProducts[0] || [];

      setTimeout(() => {
        iframe.contentWindow?.postMessage(
          {
            type: "PRODUCTS_DATA",
            products: productsData,
          },
          window.location.origin
        );
      }, 100);
    }
  },

  reloadCreative(refreshClicked: boolean) {
    const searchParams = this.getSearchParams();

    dismissClickthroughOverlays();

    // Reset any native footer from a previous render (published-native or
    // raw-asset native). Re-added below when native=1 is present.
    this.clearNativeFooter();

    this.useFullscreenPreview =
      /iPhone|iPad|Android/i.test(navigator.userAgent) ||
      searchParams.get("fullscreen") === "true";

    const iframeID = this.useFullscreenPreview
      ? "preview-iframe-mobile"
      : "preview-iframe-desktop";
    const previewIframe = document.getElementById(
      iframeID
    ) as HTMLIFrameElement;

    const assetId = searchParams.get("asset");
    if (assetId) {
      this.isRawAsset = true;
      this.creativeName = searchParams.get("name") || assetId;
      const type = searchParams.get("type") || "video";
      const file = searchParams.get("file");
      const isDev = searchParams.get("env") === "dev";
      const cdnBase = isDev
        ? "https://assets-dev.liftoff-genai.io"
        : "https://assets.liftoff-genai.io";
      const previewerBase = getPreviewersBaseUrl();

      let iframeSrc: string;
      if (type === "html") {
        const htmlFile = file ? `${file}.html` : "index.html";
        iframeSrc = `${cdnBase}/${assetId}/${htmlFile}`;
      } else {
        const params = new URLSearchParams({ id: assetId });
        if (type !== "video") params.set("format", type);
        if (file) params.set("file", file);
        // Always pass CDN origin so video/image previewers build absolute media URLs.
        // Relative `../${asset}/…` breaks when the previewer document URL does not sit
        // under `/previewers/` as assumed, which produced wrong paths like `…/video.mp4`.
        params.set("base", cdnBase);
        iframeSrc = `${previewerBase}/${type === "video" ? "video" : "image"}.html?${params.toString()}`;
      }

      const sizeParam = searchParams.get("size");
      const widthParam = parsePositiveInt(searchParams.get("w"));
      const heightParam = parsePositiveInt(searchParams.get("h"));

      let slotType: "banner" | "mrec" | null = null;
      if (sizeParam === "banner" || sizeParam === "mrec") {
        slotType = sizeParam;
      } else if (widthParam != null && heightParam != null) {
        const classified = classifyBannerDimensions(widthParam, heightParam);
        if (classified === "banner" || classified === "mrec") {
          slotType = classified;
        }
      }

      if (slotType) {
        const dims =
          widthParam != null && heightParam != null
            ? { width: widthParam, height: heightParam }
            : defaultSlotDimensions(slotType);
        this.applyBannerSlotChrome(
          dims.width,
          dims.height,
          slotType,
          searchParams
        );
      }

      previewIframe.setAttribute("src", iframeSrc);

      // Unpublished native assets (raw CDN, no Pinpoint CID) can't use Labrat,
      // so the resolver passes native=1 + the footer fields. Render the
      // Labrat-style footer inside the device frame below the hero.
      if (searchParams.get("native") === "1") {
        this.applyNativeFooter(searchParams);
      }

      this.setLoading(false);
      window.TrePreview.enableReload(true);
      return;
    }

    this.isRawAsset = false;
    this.creativeId = searchParams.get("creative") || "";
    this.isMadlib = searchParams.get("madlib") === "true";
    this.creativeName =
      searchParams.get("name") ||
      (this.isMadlib ? "Madlib: " : "") + this.creativeId;
    this.forceLandscape = searchParams.get("landscape") === "true";

    // If language selection has been performed then pass the new lang to the iframe.
    if (this.langSelector().value) {
      searchParams.set("lang", this.langSelector().value);
    }

    // Products data will be sent via postMessage instead of query string
    if (searchParams.get("vm") === "true") {
      // Let's see if this is a ViewMaster powered preview
      this.setLoading(true);
      try {
        const {
          width,
          height,
          default_platform_id: defaultPlatformId,
          creative_type: creativeType,
        } = JSON.parse(
          synchronousRequest(
            `${LIFTOFF_DOMAIN}/admin/tre-manager/getCreativeMetaData?creativeId=${this.creativeId}`
          )
        );

        if (creativeType === "native") {
          const previewHost =
            BUILD_ENV === "development"
              ? "http://localhost:9999"
              : "https://preview.liftoff-creatives.io";
          previewIframe.setAttribute(
            "src",
            `${previewHost}/?creative_id=${this.creativeId}&dashboard_view=false&preview_width=100%25&preview_height=100%25`
          );

          this.isNative = true;

          // Note(Luis): This height is used to match a similar experience to
          // native in Labrat.
          previewIframe.style.height = "335px";

          const iframeWrapper = previewIframe.parentElement as HTMLElement;
          if (iframeWrapper) {
            iframeWrapper.style.maxWidth = "100%";
            iframeWrapper.style.maxHeight = "100%";
            iframeWrapper.style.display = "flex";
            iframeWrapper.style.justifyContent = "center";
            iframeWrapper.style.alignItems = "center";
          }

          // Use ResizeObserver to keep content-container-desktop matched to screen_container.
          const screenContainer = document.getElementById(
            "screen_container"
          ) as HTMLElement;
          const container = document.getElementById(
            "content-container-desktop"
          ) as HTMLElement;

          if (screenContainer && container) {
            const syncDimensions = () => {
              const {
                offsetWidth: containerWidth,
                offsetHeight: containerHeight,
              } = screenContainer;
              Object.assign(container.style, {
                width: `${containerWidth}px`,
                height: `${containerHeight}px`,
                maxWidth: `${containerWidth}px`,
                maxHeight: `${containerHeight}px`,
                transform: "none",
                left: "0",
                top: "0",
              });
            };

            syncDimensions();

            const resizeObserver = new ResizeObserver(() => {
              syncDimensions();
            });
            resizeObserver.observe(screenContainer);
          }

          this.setLoading(false);
          return;
        }

        // Only toggle orientation on browser refresh and not tre preview
        // refresh
        if (
          searchParams.get("size") !== "banner" &&
          searchParams.get("size") !== "mrec" &&
          !refreshClicked &&
          width > height &&
          this.previewPortrait
        ) {
          this.toggleOrientation();
        }
        this.isViewMaster = true;

        let platform = searchParams.get("platform");
        if (!platform) {
          platform = defaultPlatformId === 1 ? "ios" : "android";
        }
        const platformId = platform === "android" ? 2 : 1;

        // Since this is a ViewMaster preview, make sure our ad journey guide is setup
        if (!this.adJourneyGuide) {
          this.adJourneyGuide = new AdJourneyGuide(
            this.showStateMachinePanel.bind(this)
          );
        }
        // Make sure the iframe is set in the ad journey guide
        this.adJourneyGuide?.setIframe(previewIframe);
        fetch(
          `${LIFTOFF_DOMAIN}/admin/tre-manager/getBidResponse?creativeId=${this.creativeId}&platformId=${platformId}`
        ).then((res: Response) => {
          if (res.ok) {
            res.text().then((bidResponse: string) => {
              this.bidResponse = bidResponse;

              const bidData = JSON.parse(bidResponse);
              const bid = bidData?.seatbid?.[0]?.bid?.[0];
              if (bid?.w && bid?.h) {
                const bidWidth = bid.w;
                const bidHeight = bid.h;

                const classified = classifyBannerDimensions(
                  bidWidth,
                  bidHeight
                );
                this.contentType = classified;

                if (classified === "banner" || classified === "mrec") {
                  this.applyBannerSlotChrome(
                    bidWidth,
                    bidHeight,
                    classified,
                    searchParams
                  );
                } else {
                  clearSlotIframeStyles();
                  this.applyDeviceLayout();
                }
              }

              let viewMasterSrc = viewMasterEndpoint;
              const lang = searchParams.get("lang");
              if (lang) {
                const vmUrl = new URL(viewMasterEndpoint);
                vmUrl.searchParams.set("lang", lang);
                viewMasterSrc = vmUrl.toString();
              }

              this.adJourneyGuide?.loadIframe(
                viewMasterSrc,
                this.bidResponse,
                platform || defaultPlatformId
              );
              this.setLoading(false);
            });
          }
        });
      } catch (err) {
        console.error(
          "Exception while trying to fetch creative meta data",
          err
        );
        this.setLoading(false);

        const previewContainer = document.getElementById("preview_container");
        if (previewContainer) {
          previewContainer.innerHTML = `
            <div class="no-creative-found">
              No creative found
            </div>
          `;
          previewContainer.style.visibility = "visible";
        }
      }
    } else {
      // We're good to just reload the iframe now
      previewIframe.setAttribute(
        "src",
        this.isMadlib
          ? `https://preview.liftoff-creatives.io/?fullscreen=true&creative_id=${this.creativeId}`
          : `${window.location.origin}/launch/index.html?${searchParams}`
      );

      // Send products data to iframe after it loads
      previewIframe.onload = () => {
        this.sendProductsToIframe(previewIframe);
      };

      this.setLoading(false);
    }

    // Also take the opportunity to delete the size, so the
    // actual contents take up the full size of the iframe.
    searchParams.delete("size");

    // Enable the reload button
    window.TrePreview.enableReload(true);
  },

  generateQrCode(lang: string | null) {
    try {
      const params = new URLSearchParams(window.location.search);
      params.delete("dashboard");
      params.set("fullscreen", "true");
      if (lang) params.set("lang", lang);

      // Classic fullscreen creatives use `/launch` (GameConfig + SDK). VM/Madlib, and
      // banner/mrec, use `/preview` so the phone loads the same iframe chrome as desktop
      // (narrow slot). `/launch` QR was also loading GameConfig from tresensa with 403.
      const usePreviewPage =
        params.get("vm") === "true" ||
        params.get("madlib") === "true" ||
        params.get("size") === "banner" ||
        params.get("size") === "mrec";
      const qrPath = usePreviewPage
        ? "/preview/index.html"
        : "/launch/index.html";
      const qrTargetUrl = `${window.location.origin}${qrPath}?${params.toString()}`;

      const qrCodeHolder = document.getElementById(
        "qr_code_img_holder"
      ) as HTMLElement;

      QRCode.toDataURL(qrTargetUrl, { width: 256, margin: 1 }).then(
        (dataUrl) => {
          qrCodeHolder.replaceChildren();
          const img = new Image();
          img.src = dataUrl;
          qrCodeHolder.append(img);

          const qrCodeMessage = document.getElementById(
            "qr_code_message"
          ) as HTMLElement;
          qrCodeMessage.style.display = "none";
        },
        () => {
          const messageDiv = document.getElementById(
            "qr_code_message"
          ) as HTMLElement;
          messageDiv.innerHTML = "QR Code<br>Unavailable";
        }
      );
    } catch (err) {
      const messageDiv = document.getElementById(
        "qr_code_message"
      ) as HTMLElement;
      if (messageDiv) messageDiv.innerHTML = "QR Code<br>Unavailable";
    }
  },

  onResize() {
    // If the header width is too tight, hide the creative status
    this.determineHeaderVisibility();

    // Adjust the device layout
    this.applyDeviceLayout();

    // Draw the ad journey guide
    this.adJourneyGuide?.draw();
  },

  determineHeaderVisibility() {
    // If the header width is too tight, hide the creative status
    const headerRight = document.getElementById("header_right") as HTMLElement;
    const headerLeft = document.getElementById("header_left") as HTMLElement;
    const leftWidth = this.headerLeftWidth || headerLeft.clientWidth;
    if (
      leftWidth + headerRight.clientWidth <
      document.documentElement.clientWidth * 0.96
    ) {
      headerLeft.style.display = "inline";
    } else {
      headerLeft.style.display = "none";
    }

    // And if it's super tight, hide the creative name
    const creativeName = document.getElementById(
      "creative_name"
    ) as HTMLElement;
    if (headerRight.clientWidth < document.documentElement.clientWidth * 0.96) {
      creativeName.style.visibility = "visible";
    } else {
      creativeName.style.visibility = "hidden";
    }
  },
};
// Kick it off
window.TrePreview.reloadCreative(false);

// This is probably redundant with the iframe
window.TrePreview.enableReload(true);

// NOTE(omarsh): Populate the creative info (only try, so failures don't stop execution).
try {
  window.TrePreview.populateCreativeInfo();
} catch (error) {
  console.error("some creative information failed to load", error);
}

// Setup a listener for resizing
window.addEventListener(
  "resize",
  window.TrePreview.onResize.bind(window.TrePreview)
);

// Set up the initial preview state
window.TrePreview.applyDeviceLayout();

// Make sure we get a resize in
window.TrePreview.onResize();

// Kick off the QR code generation
window.TrePreview.generateQrCode(null);

// Listen for CTA click-through events from raw asset iframes
window.addEventListener("message", (event) => {
  if (!window.TrePreview.isRawAsset) return;

  const iframeID = window.TrePreview.useFullscreenPreview
    ? "preview-iframe-mobile"
    : "preview-iframe-desktop";
  const iframe = document.getElementById(iframeID) as HTMLIFrameElement;
  if (!iframe) return;

  const isDev =
    new URLSearchParams(window.location.search).get("env") === "dev";
  const cdnBase = isDev
    ? "https://assets-dev.liftoff-genai.io"
    : "https://assets.liftoff-genai.io";
  const allowedOrigins = new Set<string>([
    new URL(cdnBase).origin,
    getPreviewersOrigin(),
  ]);
  if (BUILD_ENV === "development") {
    allowedOrigins.add(window.location.origin);
  }

  handleRawAssetCtaMessage(event, iframe, allowedOrigins);
});
